import { Injectable } from "@angular/core";
import { NavigatorAssessmentDetailModel } from "@app/navigators/models/navigator-assessment-detail-model";
import { NavigatorDocumentModel } from "@app/navigators/models/navigator-document-model";
import { NavigatorHyperlinkModel } from "@app/navigators/models/navigator-hyperlinks-model";
import { HistoryChange, HistoryModel } from "@models/history/HistoryModel";
import { HyperlinkModel } from "@models/hyperlinks/hyperlinkmodel";
import { CerrixTreeItem } from "@models/tree/CerrixTreeItem";
import { ApiDataService } from "@services/http/ApiDataService";
import { HistoryDataService } from "@services/http/history/HistoryDataService";
import { Observable, firstValueFrom } from "rxjs";
import { BusinessNavigatorDetailModel } from "../models/business-navigator-detail-model";
import { BusinessNavigatorOverviewModel } from "../models/business-navigator-overview-model";
import { BusinessNavigatorPermissionModel } from "../models/business-navigator-permission-model";
import { BusinessNavigatorRequestModel } from "../models/business-navigator-request-model";
import { BusinessNavigatorReviewModel } from "../models/business-navigator-review-model";

@Injectable()
export class BusinessNavigatorDataService extends ApiDataService implements HistoryDataService {
    constructor() {
        super("businessnavigator");
    }

    //#region Main data calls

    getPermissions(organizationID: number, businessID: number) {
        const model = <BusinessNavigatorRequestModel>{
            organizationID: organizationID,
            businessID: businessID,
        };

        const url = this.formatUrl(model, "/permissions");
        return firstValueFrom(this.get<BusinessNavigatorPermissionModel>(url));
    }

    getOverview(): Promise<BusinessNavigatorOverviewModel[]> {
        return firstValueFrom(this.get<BusinessNavigatorOverviewModel[]>());
    }

    getRequestValidation(model: BusinessNavigatorRequestModel) {
        const url = this.formatUrl(model, "/request-validation");
        return firstValueFrom(this.get<BusinessNavigatorRequestModel>(url));
    }

    getAssessmentDetails(model: BusinessNavigatorRequestModel) {
        const url = this.formatUrl(model, "/assessment");
        return firstValueFrom(this.get<NavigatorAssessmentDetailModel>(url));
    }

    getDetails(model: BusinessNavigatorRequestModel) {
        const url = this.formatUrl(model, "/details");
        return firstValueFrom(this.get<BusinessNavigatorDetailModel>(url));
    }

    storeAssessmentDetails(
        model: BusinessNavigatorRequestModel,
        toPost: NavigatorAssessmentDetailModel
    ) {
        const url = this.formatUrl(model, "/assessment");
        return firstValueFrom(this.post<NavigatorAssessmentDetailModel>(toPost, url));
    }

    public getWidgetInfo(filters: any) {
        const url = `/widgetinfo`;
        return this.post<any[]>(filters, url);
    }

    public deleteAssessment(model: BusinessNavigatorRequestModel) {
        const url = this.formatUrl(model, "/delete");
        return this.delete(url);
    }

    //#endregion Main data Calls

    //#region Documents calls

    getDocuments(model: BusinessNavigatorRequestModel) {
        const url = `/documents/${model.businessID}`;
        return this.get<NavigatorDocumentModel>(url);
    }

    storeDocuments(toPost: NavigatorDocumentModel) {
        const url = `/documents`;
        return this.postWithDocuments<NavigatorDocumentModel>(toPost, toPost.documents, url);
    }

    getAssessmentDocuments(model: BusinessNavigatorRequestModel) {
        const url = `/assessmentdocuments/${model.businessAssessmentID}`;
        return this.get<NavigatorDocumentModel>(url);
    }

    storeAssessmentDocuments(toPost: NavigatorDocumentModel) {
        const url = `/assessmentdocuments`;
        return this.postWithDocuments<NavigatorDocumentModel>(toPost, toPost.documents, url);
    }

    ////#endregion Document calls

    //#region Hyperlink calls
    getHyperlinks(model: BusinessNavigatorRequestModel) {
        const url = `/hyperlinks/${model.businessID}`;
        return this.get<HyperlinkModel[]>(url);
    }

    storeHyperlinks(model: NavigatorHyperlinkModel) {
        const url = `/hyperlinks`;
        return this.post<NavigatorHyperlinkModel>(model, url);
    }
    ////#endregion Hyperlink calls

    //#region Standing data calls

    public getOrganizations() {
        const url = this.actionUrl + `/standingdata/organizations`;
        return this.http.get<CerrixTreeItem[]>(url);
    }

    public getBusinessDimension(bdId: number) {
        const url = `/businessdimension/${bdId}`;
        return this.get<any[]>(url);
    }

    public getBusinessDimensionsByOrganization(selectedOrganization: number) {
        let url = "/standingdata/business";
        if (selectedOrganization) {
            url += `?organization=${selectedOrganization}`;
        }

        return this.get<CerrixTreeItem[]>(url);
    }

    //#end Standing data calls

    //#region Review Data calls

    isReviewAllowed(model: BusinessNavigatorRequestModel) {
        const url = this.formatUrl(model, "/review/allowed");
        return firstValueFrom(this.get<boolean>(url));
    }

    storeReview(model: BusinessNavigatorRequestModel, toPost: BusinessNavigatorReviewModel) {
        const url = this.formatUrl(model, "/review");
        return firstValueFrom(this.post(toPost, url));
    }

    getReviews(model: BusinessNavigatorRequestModel) {
        const url = this.formatUrl(model, "/review");
        return firstValueFrom(this.get<BusinessNavigatorRequestModel>(url));
    }

    deleteReview(model: BusinessNavigatorRequestModel, reviewID: number) {
        const url = this.formatUrl(model, "/review/" + reviewID);
        return firstValueFrom(this.delete(url));
    }

    //#endregion Review Data calls

    public getScores() {
        const url = `/standingdata/scores`;
        return this.get<any[]>(url);
    }

    private formatUrl(model: BusinessNavigatorRequestModel, path: string) {
        if (!model.organizationID && !model.businessID) {
            throw new Error("Organization or Business required!");
        }

        const orgID = model.organizationID ? model.organizationID : "null";
        const bdID = model.businessID ? model.businessID : "null";

        const params = `?orgID=${orgID}&bdID=${bdID}`;
        const url = path + params;

        return url;
    }

    //#region Module data calls

    getRisks(model: BusinessNavigatorRequestModel) {
        const url = this.formatUrl(model, "/moduledata/risks");
        return this.get<any>(url);
    }

    public getLinkedControlsByRiskId(riskGuid: string) {
        const url = `/${riskGuid}/linkedcontrols`;
        return this.get<any>(url);
    }

    getControls(model: BusinessNavigatorRequestModel) {
        const url = this.formatUrl(model, "/moduledata/controls");
        return this.get<any>(url);
    }

    getEvents(model: BusinessNavigatorRequestModel) {
        const url = this.formatUrl(model, "/moduledata/events");
        return this.get<any>(url);
    }

    getMois(model: BusinessNavigatorRequestModel) {
        const url = this.formatUrl(model, "/moduledata/mois");
        return this.get<any>(url);
    }

    getKris(model: BusinessNavigatorRequestModel) {
        const url = this.formatUrl(model, "/moduledata/kris");
        return this.get<any>(url);
    }

    getDataProcessing(model: BusinessNavigatorRequestModel) {
        const url = this.formatUrl(model, "/moduledata/dataprocessing");
        return this.get<any>(url);
    }

    getThirdParties(model: BusinessNavigatorRequestModel) {
        const url = this.formatUrl(model, "/moduledata/thirdparties");
        return this.get<any>(url);
    }

    //#endregion Module data calls

    //#region History

    public getHistory(businessAssessmentID: number): Observable<HistoryModel[]> {
        return this.get<HistoryModel[]>(`/${businessAssessmentID}/history`);
    }

    public getHistoryChanges(
        businessAssessmentID: string | number,
        historyDate: string | number
    ): Observable<HistoryChange[]> {
        return this.get<HistoryChange[]>(`/${businessAssessmentID}/history/${historyDate}`);
    }

    //#region History
}
